import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import '../App.scss';
import getWinnersWeekly from '../helpers/getWinnersWeekly';
import getWinners from '../helpers/getWinners';
import DatePicker from './DatePicker';
import moment from 'moment';
import getWinner from '../helpers/getWinner';

interface Winner {
  phone: string;
  award: string;
}

const WinnersSection: React.FC = () => {
  const [selectedView, setSelectedView] = useState<'daily' | 'weekly' | 'main'>('daily');
  // Sredina reda: null = dugme GLAVNA NAGRADA, inace odabir datuma ili nedelje.
  const [activePicker, setActivePicker] = useState<'daily' | 'weekly' | null>(null);
  const [selectedWeek, setSelectedWeek] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<moment.Moment>(moment().subtract(1, 'day'));
  const [winners, setWinners] = useState<Winner[]>([]);
  const [message, setMessage] = useState<string>("");
  const [mainWinner, setMainWinner] = useState<Winner | null>(null);
  const [weekMenuOpen, setWeekMenuOpen] = useState(false);
  const [weekMenuDown, setWeekMenuDown] = useState(false);
  const weekMenuRef = useRef<HTMLDivElement>(null);

  const weekOptions = Array.from({ length: 5 }, (_, index) => index + 1);

  useEffect(() => {
    if (activePicker !== 'weekly') {
      setWeekMenuOpen(false);
    }
  }, [activePicker]);

  useEffect(() => {
    if (!weekMenuOpen) return;

    const closeMenu = (event: MouseEvent) => {
      if (!weekMenuRef.current?.contains(event.target as Node)) {
        setWeekMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, [weekMenuOpen]);

  useEffect(() => {
    const emptyMessage = "Nema dobitnika za odabrani prikaz";

    const fetchWinners = async () => {
      if (selectedView === "main") {
        setWinners([]);
        const response = await getWinner();
        if (response.length > 0) {
          setMainWinner(response[0]);
          setMessage("");
        } else {
          setMainWinner(null);
          setMessage(emptyMessage);
        }
        return;
      }

      setMainWinner(null);
      let response = [];
      if (selectedView === "weekly") {
        response = await getWinnersWeekly(selectedWeek);
      } else if (selectedView === "daily" && selectedDate) {
        const formattedDate = selectedDate.format("YYYYMMDD");
        response = await getWinners(formattedDate);
      }

      if (response.length === 0) {
        setMessage(emptyMessage);
        setWinners([]);
      } else {
        setMessage("");
        setWinners(response);
      }
    };

    fetchWinners();
  }, [selectedView, selectedWeek, selectedDate]);

  const handleMainPrizeClick = () => {
    setActivePicker(null);
    setSelectedView("main");
  };

  const toggleWeekMenu = () => {
    if (weekMenuOpen) {
      setWeekMenuOpen(false);
      return;
    }

    if (window.innerWidth > 991 && weekMenuRef.current) {
      const rect = weekMenuRef.current.getBoundingClientRect();
      setWeekMenuDown(window.innerHeight - rect.bottom > rect.top);
    } else {
      setWeekMenuDown(false);
    }

    setWeekMenuOpen(true);
  };


  return (
    <section id="lista-dobitnika" className="winners-section">
      <Container>
        <Row className="justify-content-center">
          <Col md={10}>
            <div className="winners-table-wrapper">
              <div className="winners-frame">
                <h2 className="winners-title">LISTA DOBITNIKA</h2>
                <div className="winners-table">
                  {mainWinner ? (
                    <div className="main-winner custom-font">
                      Glavna nagrada: <strong>{mainWinner.phone}</strong> -{' '}
                      {mainWinner.award}
                    </div>
                  ) : message ? (
                    <div className="no-winners-message">{message}</div>
                  ) : (
                    <table className={`custom-font is-${selectedView}`}>
                      <tbody>
                        {winners.map((winner, index) => (
                          <tr
                            key={index}
                            className={index % 2 === 0 ? "even" : "odd"}
                          >
                            <td>{index + 1}.</td>
                            <td>{winner.phone}</td>
                            <td>{winner.award}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
                <div className="winners-header">
                  <button
                    className={`awards-button${activePicker === 'daily' ? ' is-active' : ''}`}
                    onClick={() => {
                      setMainWinner(null);
                      setSelectedView('daily');
                      setActivePicker(activePicker === 'daily' ? null : 'daily');
                    }}
                  >
                    DNEVNE <span className="awards-btn-rest">NAGRADE</span>
                  </button>

                  <div className="winners-control">
                    {activePicker === null && (
                      <button
                        className="awards-button"
                        onClick={handleMainPrizeClick}
                      >
                        GLAVNA <span className="awards-btn-rest">NAGRADA</span>
                      </button>
                    )}
                    {activePicker === 'daily' && (
                      <div className="winners-picker">
                        <DatePicker
                          setDate={(date: moment.Moment) => setSelectedDate(date)}
                        />
                        <button
                          type="button"
                          className="picker-reset"
                          aria-label="Prikaži glavnu nagradu"
                          onClick={() => setActivePicker(null)}
                        >
                          &times;
                        </button>
                      </div>
                    )}
                    {activePicker === 'weekly' && (
                      <div className="winners-picker">
                        <div className="week-dropdown-wrap" ref={weekMenuRef}>
                          <button
                            type="button"
                            className="week-dropdown"
                            aria-haspopup="listbox"
                            aria-expanded={weekMenuOpen}
                            onClick={toggleWeekMenu}
                          >
                            {selectedWeek}. nedelja
                          </button>
                          {weekMenuOpen && (
                            <ul
                              className={`week-dropdown-list${weekMenuDown ? " is-down" : ""}`}
                              role="listbox"
                            >
                              {weekOptions.map((week) => (
                                <li key={week}>
                                  <button
                                    type="button"
                                    role="option"
                                    aria-selected={week === selectedWeek}
                                    className={week === selectedWeek ? 'is-selected' : ''}
                                    onClick={() => {
                                      setSelectedWeek(week);
                                      setWeekMenuOpen(false);
                                    }}
                                  >
                                    {week}. nedelja
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <button
                          type="button"
                          className="picker-reset"
                          aria-label="Prikaži glavnu nagradu"
                          onClick={() => setActivePicker(null)}
                        >
                          &times;
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    className={`awards-button${activePicker === 'weekly' ? ' is-active' : ''}`}
                    onClick={() => {
                      setMainWinner(null);
                      setSelectedView('weekly');
                      setActivePicker(activePicker === 'weekly' ? null : 'weekly');
                    }}
                  >
                    NEDELJNE <span className="awards-btn-rest">NAGRADE</span>
                  </button>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default WinnersSection;
